// src/admin/FormEditor/utils/formTemplates.js
import { CommonRegexPatterns } from "../../../constants/common";
import { translateToSimplifiedChinese } from "../../../utils/chineseTranslation.js";
import {
  get_yyyymmddHHMMSS,
  getFormattedDateTime,
  oneYearLater2359_hkt,
  today0000_hkt,
} from "../../../utils/datetimeUtil.js";
import { convertQuillAlignmentToInlineStyles } from "../../../utils/quillConversion.js";

const DEFAULT_AVAILABLE_LANGUAGES = ["en", "tc", "sc"];

const DEFAULT_VALIDATION = {
  min_length: null,
  max_length: 200,
  min_rows: null,
  max_rows: null,
  regex: {
    pattern: null,
    example: null,
  },
  min_selections: null,
  max_selections: null,
  other_max_length: 200,
};

const DEFAULT_ACKNOWLEDGMENTS = {
  Event: {
    en: "Thank you for your registration. \nAn acknowledgement email has been sent to you.",
    tc: "感謝您的登記。\n確認電子郵件已發送給您。",
    sc: "感谢您的登记。\n确认电子邮件已发送给您。",
  },
  Survey: {
    en: "Thank you for completing the survey.",
    tc: "感謝您完成問卷調查。",
    sc: "感谢您完成问卷调查。",
  },
};

const DEFAULT_PICS_CONTENT = {
  Event: {
    en: `
<p><strong>PERSONAL INFORMATION COLLECTION STATEMENT</strong></p><p><br></p><p><strong><u>Purpose of Collection</u></strong></p><p><br></p><p class="ql-indent-1 hanging-indent-true">1.&nbsp;&nbsp;&nbsp;&nbsp;The personal data collected in this form will be used by the Land Registry for the following purposes:-</p><p class="ql-indent-2 hanging-indent-true">1.1&nbsp;&nbsp;to process the application for enrolment to the subject event organised by the Land Registry;</p><p class="ql-indent-2 hanging-indent-true">1.2&nbsp;&nbsp;to facilitate communications; and</p><p class="ql-indent-2 hanging-indent-true">1.3&nbsp;&nbsp;to produce statistics relating to the Land Registry’s services.</p><p class="ql-indent-1"><br></p><p class="ql-indent-1">You understand that the provision of personal data is obligatory.&nbsp;If you fail to provide information as required in this form, the Land Registry may not be able to process your application.</p><p class="ql-indent-1">Please do NOT provide any personal data (including personal data relating to third parties) which are not specifically required to be submitted.&nbsp;Where information of any third party is included in this form or any document(s) filed in relation to it, the Land Registry will treat that you have obtained consent from such third party to disclose such information for the purposes above.</p><p><br></p><p><strong><u>Disclosure of Personal Data</u></strong></p><p><br></p><p class="ql-indent-1 hanging-indent-true">2.&nbsp;&nbsp;&nbsp;&nbsp;You understand that the personal data provided may be disclosed or transferred to relevant parties where such disclosure or transfer is necessary for the purposes as stated in paragraph 1 above.</p><p><br></p><p><strong><u>Access to Personal Data</u></strong></p><p><br></p><p class="ql-indent-1 hanging-indent-true">3.&nbsp;&nbsp;&nbsp;&nbsp;You understand that pursuant to Sections 18 and 22 and Principle 6 of Schedule 1 to the Personal Data (Privacy) Ordinance (Cap. 486) (“PDPO”), you have the right to request access to and correction of your personal data held by the Land Registry.&nbsp;The Land Registry may charge a fee to process the said request in accordance with the PDPO.&nbsp;Any such request shall be made to the Personal Data (Privacy) Officer of the Land Registry at 28th Floor, Queensway Government Offices, 66 Queensway, Hong Kong.</p><h2><br></h2>
  `,
    tc: `
<p><strong>個人資料收集聲明</strong></p><p><br></p><p><strong><u>收集目的</u></strong></p><p><br></p><p class="hanging-indent-true ql-indent-1">1.&nbsp;&nbsp;&nbsp;土地註冊處會把這份表格所載的個人資料用於以下用途：-</p><p class="hanging-indent-true ql-indent-2">1.1&nbsp;&nbsp;&nbsp;處理由土地註冊處舉辦有關活動的報名申請；</p><p class="hanging-indent-true ql-indent-2">1.2&nbsp;&nbsp;&nbsp;方便進行聯絡；以及</p><p class="hanging-indent-true ql-indent-2">1.3&nbsp;&nbsp;&nbsp;製備與土地註冊處服務有關的統計資料。</p><p class="ql-indent-1"><br></p><p class="ql-indent-1">你明白提供個人資料是必須的。倘若你不提供所要求的資料，本處可能無法提供所要求的服務。</p><p class="ql-indent-1">請勿提供任何沒有明確規定必須提交的個人資料(包括關乎第三者的個人資料)。如於本表格或與其有關而存檔的文件中包含任何第三者的資料，本處將視作你已獲該第三者同意披露該等資料，以用於上述目的。</p><p><br></p><p><strong><u>個人資料的披露</u></strong></p><p><br></p><p class="hanging-indent-true ql-indent-1">2.&nbsp;&nbsp;&nbsp;&nbsp;你明白為達到上文第一段中所述明的目的而有需要時，你所提供的個人資料可能會披露或轉交予相關人士。該等個人資料亦可能會在《個人資料(私隱)條例》(第486章)許可下披露或轉交予執法機關。</p><p><br></p><p><strong><u>查閱個人資料</u></strong></p><p><br></p><p class="hanging-indent-true ql-indent-1">3.&nbsp;&nbsp;&nbsp;&nbsp;你明白根據《個人資料(私隱)條例》(第 486章)第18和第22條及附表1第6原則，你有權要求查閱及更正本處所持有你的個人資料。本處可根據該條例就處理該等要求收取費用。任何該等要求可向本處的個人資料(私隱)主任提出(地址：香港金鐘道66號金鐘道政府合署28樓土地註冊處)。</p><h2><br></h2>
  `,
    sc: `
<p><strong>个人资料收集声明</strong></p><p><br></p><p><strong><u>收集目的</u></strong></p><p><br></p><p class="hanging-indent-true ql-indent-1">1.&nbsp;&nbsp;&nbsp;土地注册处会把这份表格所载的个人资料用于以下用途：-</p><p class="hanging-indent-true ql-indent-2">1.1&nbsp;&nbsp;&nbsp;处理由土地注册处举办有关活动的报名申请；</p><p class="hanging-indent-true ql-indent-2">1.2&nbsp;&nbsp;&nbsp;方便进行联络；以及</p><p class="hanging-indent-true ql-indent-2">1.3&nbsp;&nbsp;&nbsp;製备与土地注册处服务有关的统计资料。</p><p class="ql-indent-1"><br></p><p class="ql-indent-1">你明白提供个人资料是必须的。倘若你不提供所要求的资料，本处可能无法提供所要求的服务。</p><p class="ql-indent-1">请勿提供任何没有明确规定必须提交的个人资料(包括关乎第三者的个人资料)。如于本表格或与其有关而存档的文件中包含任何第三者的资料，本处将视作你已获该第三者同意披露该等资料，以用于上述目的。</p><p><br></p><p><strong><u>个人资料的披露</u></strong></p><p><br></p><p class="hanging-indent-true ql-indent-1">2.&nbsp;&nbsp;&nbsp;&nbsp;你明白为达到上文第一段中所述明的目的而有需要时，你所提供的个人资料可能会披露或转交予相关人士。该等个人资料亦可能会在《个人资料(私隐)条例》(第486章)许可下披露或转交予执法机关。</p><p><br></p><p><strong><u>查阅个人资料</u></strong></p><p><br></p><p class="hanging-indent-true ql-indent-1">3.&nbsp;&nbsp;&nbsp;&nbsp;你明白根据《个人资料(私隐)条例》(第 486章)第18和第22条及附表1第6原则，你有权要求查阅及更正本处所持有你的个人资料。本处可根据该条例就处理该等要求收取费用。任何该等要求可向本处的个人资料(私隐)主任提出(地址：香港金钟道66号金钟道政府合署28楼土地注册处)。</p><h2><br></h2>
  `,
  },
  Survey: {
    en: `
<p><strong>PERSONAL INFORMATION COLLECTION STATEMENT</strong></p><p><br></p><p><strong><u>Purpose of Collection</u></strong></p><p><br></p><p class="ql-indent-1 hanging-indent-true">1.&nbsp;&nbsp;&nbsp;&nbsp;The personal data collected in this form will be used by the Land Registry for the following purposes:-</p><p class="ql-indent-2 hanging-indent-true">1.1&nbsp;&nbsp;to process the application for enrolment to the subject event organised by the Land Registry;</p><p class="ql-indent-2 hanging-indent-true">1.2&nbsp;&nbsp;to facilitate communications; and</p><p class="ql-indent-2 hanging-indent-true">1.3&nbsp;&nbsp;to produce statistics relating to the Land Registry’s services.</p><p class="ql-indent-1"><br></p><p class="ql-indent-1">You understand that the provision of personal data is obligatory.&nbsp;If you fail to provide information as required in this form, the Land Registry may not be able to process your questionnaire.</p><p class="ql-indent-1">Please do NOT provide any personal data (including personal data relating to third parties) which are not specifically required to be submitted.&nbsp;Where information of any third party is included in this form or any document(s) filed in relation to it, the Land Registry will treat that you have obtained consent from such third party to disclose such information for the purposes above.</p><p><br></p><p><strong><u>Disclosure of Personal Data</u></strong></p><p><br></p><p class="ql-indent-1 hanging-indent-true">2.&nbsp;&nbsp;&nbsp;&nbsp;You understand that the personal data provided may be disclosed or transferred to relevant parties where such disclosure or transfer is necessary for the purposes as stated in paragraph 1 above.</p><p><br></p><p><strong><u>Access to Personal Data</u></strong></p><p><br></p><p class="ql-indent-1 hanging-indent-true">3.&nbsp;&nbsp;&nbsp;&nbsp;You understand that pursuant to Sections 18 and 22 and Principle 6 of Schedule 1 to the Personal Data (Privacy) Ordinance (Cap. 486) (“PDPO”), you have the right to request access to and correction of your personal data held by the Land Registry.&nbsp;The Land Registry may charge a fee to process the said request in accordance with the PDPO.&nbsp;Any such request shall be made to the Personal Data (Privacy) Officer of the Land Registry at 28th Floor, Queensway Government Offices, 66 Queensway, Hong Kong.</p><h2><br></h2>
  `,
    tc: `
<p><strong>個人資料收集聲明</strong></p><p><br></p><p><strong><u>收集目的</u></strong></p><p><br></p><p class="hanging-indent-true ql-indent-1">1.&nbsp;&nbsp;&nbsp;土地註冊處會把這份表格所載的個人資料用於以下用途：-</p><p class="hanging-indent-true ql-indent-2">1.1&nbsp;&nbsp;&nbsp;處理由土地註冊處舉辦有關活動的報名申請；</p><p class="hanging-indent-true ql-indent-2">1.2&nbsp;&nbsp;&nbsp;方便進行聯絡；以及</p><p class="hanging-indent-true ql-indent-2">1.3&nbsp;&nbsp;&nbsp;製備與土地註冊處服務有關的統計資料。</p><p class="ql-indent-1"><br></p><p class="ql-indent-1">你明白提供個人資料是必須的。倘若你不提供所要求的資料，本處可能無法處理你的問卷。</p><p class="ql-indent-1">請勿提供任何沒有明確規定必須提交的個人資料(包括關乎第三者的個人資料)。如於本表格或與其有關而存檔的文件中包含任何第三者的資料，本處將視作你已獲該第三者同意披露該等資料，以用於上述目的。</p><p><br></p><p><strong><u>個人資料的披露</u></strong></p><p><br></p><p class="hanging-indent-true ql-indent-1">2.&nbsp;&nbsp;&nbsp;&nbsp;你明白為達到上文第一段中所述明的目的而有需要時，你所提供的個人資料可能會披露或轉交予相關人士。該等個人資料亦可能會在《個人資料(私隱)條例》(第486章)許可下披露或轉交予執法機關。</p><p><br></p><p><strong><u>查閱個人資料</u></strong></p><p><br></p><p class="hanging-indent-true ql-indent-1">3.&nbsp;&nbsp;&nbsp;&nbsp;你明白根據《個人資料(私隱)條例》(第 486章)第18和第22條及附表1第6原則，你有權要求查閱及更正本處所持有你的個人資料。本處可根據該條例就處理該等要求收取費用。任何該等要求可向本處的個人資料(私隱)主任提出(地址：香港金鐘道66號金鐘道政府合署28樓土地註冊處)。</p><h2><br></h2>
  `,
    sc: `
<p><strong>个人资料收集声明</strong></p><p><br></p><p><strong><u>收集目的</u></strong></p><p><br></p><p class="hanging-indent-true ql-indent-1">1.&nbsp;&nbsp;&nbsp;土地注册处会把这份表格所载的个人资料用于以下用途：-</p><p class="hanging-indent-true ql-indent-2">1.1&nbsp;&nbsp;&nbsp;处理由土地注册处举办有关活动的报名申请；</p><p class="hanging-indent-true ql-indent-2">1.2&nbsp;&nbsp;&nbsp;方便进行联络；以及</p><p class="hanging-indent-true ql-indent-2">1.3&nbsp;&nbsp;&nbsp;製备与土地注册处服务有关的统计资料。</p><p class="ql-indent-1"><br></p><p class="ql-indent-1">你明白提供个人资料是必须的。倘若你不提供所要求的资料，本处可能无法处理你的问卷。</p><p class="ql-indent-1">请勿提供任何没有明确规定必须提交的个人资料(包括关乎第三者的个人资料)。如于本表格或与其有关而存档的文件中包含任何第三者的资料，本处将视作你已获该第三者同意披露该等资料，以用于上述目的。</p><p><br></p><p><strong><u>个人资料的披露</u></strong></p><p><br></p><p class="hanging-indent-true ql-indent-1">2.&nbsp;&nbsp;&nbsp;&nbsp;你明白为达到上文第一段中所述明的目的而有需要时，你所提供的个人资料可能会披露或转交予相关人士。该等个人资料亦可能会在《个人资料(私隐)条例》(第486章)许可下披露或转交予执法机关。</p><p><br></p><p><strong><u>查阅个人资料</u></strong></p><p><br></p><p class="hanging-indent-true ql-indent-1">3.&nbsp;&nbsp;&nbsp;&nbsp;你明白根据《个人资料(私隐)条例》(第 486章)第18和第22条及附表1第6原则，你有权要求查阅及更正本处所持有你的个人资料。本处可根据该条例就处理该等要求收取费用。任何该等要求可向本处的个人资料(私隐)主任提出(地址：香港金钟道66号金钟道政府合署28楼土地注册处)。</p><h2><br></h2>
  `,
  },
};

let _currentIndex = 0;
const generateObjectId = (prefix = "id_") => {
  //const randomNum = Math.floor(Math.random() * 90000) + 10000;
  const id = `${prefix}${get_yyyymmddHHMMSS()}${_currentIndex}`;
  _currentIndex = _currentIndex + 1;
  return id;
};

const createEmptyLanguageObject = (languages = DEFAULT_AVAILABLE_LANGUAGES) => {
  return Object.fromEntries(languages.map((lang) => [lang, ""]));
};

const isLanguageObject = (obj) => {
  if (!obj || typeof obj !== "object") return false;
  const keys = Object.keys(obj);
  return keys.some((key) => DEFAULT_AVAILABLE_LANGUAGES.includes(key));
};

const createDefaultQuestion = (languages, purpose, type, questionData = {}) => {
  const always_display_answer_new_line = purpose == "Survey" ? true : false;
  const options_display_inline = purpose == "Survey" ? true : false;
  return {
    object_id: generateObjectId("q_"),
    field_name: "",
    custom_report_header: "",
    mutex_id: "",
    type,
    required: false,
    always_display_answer_new_line,
    options_display_inline,
    expand_rows_in_report: false,
    max_count: 5,
    enable_rtf_title: false,
    title_align: "left",
    rtf_title: createEmptyLanguageObject(languages),
    count_notice: createEmptyLanguageObject(languages),
    title: createEmptyLanguageObject(languages),
    description: createEmptyLanguageObject(languages),
    mandatory_warning: createEmptyLanguageObject(languages),
    with_areacode: false,
    validation: { ...DEFAULT_VALIDATION },
    ...questionData,
  };
};

const createDefaultOption = (languages = DEFAULT_AVAILABLE_LANGUAGES) => {
  return {
    object_id: generateObjectId("opt_"),
    custom_report_header: "",
    desc: createEmptyLanguageObject(languages),
    exclude_mutex: false,
    dominating_option: false,
    other_option: false,
    other_option_placeholder: createEmptyLanguageObject(languages),
  };
};

const createTemplateQuestion = (template) => {
  let question = createDefaultQuestion(DEFAULT_AVAILABLE_LANGUAGES);

  switch (template) {
    case "salutation":
      const salutationQuestion = {
        field_name: "salutation",
        type: "dropdown",
        required: true,
        title: {
          en: "Salutation",
          tc: "稱謂",
          sc: "称谓",
        },
        options: [
          {
            desc: {
              en: "Mr.",
              tc: "先生",
              sc: "先生",
            },
          },
          {
            desc: {
              en: "Ms.",
              tc: "女士",
              sc: "女士",
            },
          },
        ],
      };
      question = { ...question, ...salutationQuestion };
      break;

    case "surname":
      const surnameQuestion = {
        field_name: "surname",
        type: "text",
        required: true,
        title: {
          en: "Surname",
          tc: "姓氏",
          sc: "姓氏",
        },
        validation: {
          min_length: null,
          max_length: 100,
          min_rows: null,
          max_rows: null,
          regex: {
            pattern: null,
            example: null,
          },
          min_selections: null,
          max_selections: null,
        },
      };

      question = { ...question, ...surnameQuestion };
      break;

    case "given_name":
      const givenNameQuestion = {
        field_name: "given_name",
        type: "text",
        required: true,
        title: {
          en: "Given Name",
          tc: "名字",
          sc: "名字",
        },
        validation: {
          min_length: null,
          max_length: 100,
          min_rows: null,
          max_rows: null,
          regex: {
            pattern: null,
            example: null,
          },
          min_selections: null,
          max_selections: null,
        },
      };

      question = { ...question, ...givenNameQuestion };

      break;

    case "email":
      const emailQuestion = {
        object_id: 1748999854325,
        field_name: "email",
        type: "text",
        required: true,
        title: {
          en: "Email Address",
          tc: "電郵地址",
          sc: "电邮地址",
        },
        description: {
          en: "",
          tc: "",
          sc: "",
        },
        validation: {
          min_length: 1,
          max_length: 100,
          regex: {
            pattern: "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z0-9]{1,}$",
            example: "name@example.com",
          },
          min_selections: null,
          max_selections: null,
        },
        options: [],
      };

      question = { ...question, ...emailQuestion };
      break;

    case "phone":
      const with_areacode = false;
      const pattern = CommonRegexPatterns.find(
        (p) =>
          p.label ===
          (with_areacode ? "Phone Number with Areacode" : "Phone Number")
      );

      const phoneValidation = {
        min_length: 8,
        max_length: 20,
        regex: { pattern: pattern?.value, example: "" },
      };

      const phoneQuestion = {
        field_name: "Telephone_Number",
        type: "phone_number",
        required: false,
        title: {
          en: "Telephone Number",
          tc: "電話號碼",
          sc: "电话号码",
        },
        description: {
          en: "",
          tc: "",
          sc: "",
        },
        validation: phoneValidation,
        options: [],
      };

      question = { ...question, ...phoneQuestion };
      break;

    default:
      break;
  }

  console.log(question);
  return question;
};

const prepareNewFormMetadata = (purpose = "Event") => {
  const acknowledgment = DEFAULT_ACKNOWLEDGMENTS[purpose];
  const picsContent = DEFAULT_PICS_CONTENT[purpose];
  const form_id = `form_${get_yyyymmddHHMMSS()}`;

  const valid_from_time = getFormattedDateTime(
    today0000_hkt()
  ).yyyy_mm_dd_HH_MM_SS;

  const valid_from = {
    time: valid_from_time,
    msg: createEmptyLanguageObject(DEFAULT_AVAILABLE_LANGUAGES),
  };
  const valid_to_time = getFormattedDateTime(
    oneYearLater2359_hkt()
  ).yyyy_mm_dd_HH_MM_SS;
  const valid_to =
    purpose == "Survey"
      ? {
          time: valid_to_time,
          msg: {
            en: "The survey is closed.",
            tc: "問卷調查已截止。",
            sc: "问卷调查已截止。",
          },
        }
      : {
          time: valid_to_time,
          msg: createEmptyLanguageObject(DEFAULT_AVAILABLE_LANGUAGES),
        };

  const sendEmail = purpose == "Survey" ? false : true;
  const sendEmailFieldName = "email";

  return {
    form_id,
    object_id: generateObjectId("form_"),
    template: "default",
    purpose,
    subform: false,
    supported_languages: DEFAULT_AVAILABLE_LANGUAGES,
    valid_from,
    valid_to,
    event_name: createEmptyLanguageObject(DEFAULT_AVAILABLE_LANGUAGES),
    title: createEmptyLanguageObject(DEFAULT_AVAILABLE_LANGUAGES),
    contact_email: "csa@landreg.gov.hk",
    description: createEmptyLanguageObject(DEFAULT_AVAILABLE_LANGUAGES),
    details: createEmptyLanguageObject(DEFAULT_AVAILABLE_LANGUAGES),
    acknowledgment,
    logo_base64: createEmptyLanguageObject(DEFAULT_AVAILABLE_LANGUAGES),
    logo_url: {
      en: "/regform/image/lr-logo-en.png",
      tc: "/regform/image/lr-logo-tc.png",
      sc: "/regform/image/lr-logo-sc.png",
    },
    banner_base64: createEmptyLanguageObject(DEFAULT_AVAILABLE_LANGUAGES),
    banner_url: createEmptyLanguageObject(DEFAULT_AVAILABLE_LANGUAGES),
    acknowledgmentDisplay: "popup",
    acknowledgmentRedirectUrl: "",
    sendEmail,
    sendEmailFieldName,
    clearBtn: true,
    requireCaptcha: true,
    pics: {
      enabled: true,
      content: picsContent,
      style: {
        maxHeight: "200px", // default height for scrollable area
        mobileMaxHeight: "150px", // default height for mobile
      },
    },
    questions: [],
  };
};

function recursiveFillMissingLanguageContent(
  obj,
  supported_languages,
  fallbackOrder = ["tc", "sc", "en"]
) {
  // Create a deep copy of the object at the top level
  const newObj = JSON.parse(JSON.stringify(obj));

  function fillLanguageContent(objToFill) {
    for (let key in objToFill) {
      // Handle arrays
      if (Array.isArray(objToFill[key])) {
        objToFill[key].forEach((item, index) => {
          fillLanguageContent(item);
        });
        continue;
      }

      // Handle objects
      if (typeof objToFill[key] === "object" && objToFill[key] !== null) {
        if (isLanguageObject(objToFill[key])) {
          const langObj = { ...objToFill[key] };

          // For each supported language that has empty content
          supported_languages.forEach((lang) => {
            if (!langObj[lang] || langObj[lang].trim() === "") {
              // Try to find non-empty content following fallback order
              for (const fallbackLang of fallbackOrder) {
                if (
                  fallbackLang !== lang &&
                  langObj[fallbackLang] &&
                  langObj[fallbackLang].trim() !== ""
                ) {
                  langObj[lang] = langObj[fallbackLang];
                  break;
                }
              }
            }
          });

          objToFill[key] = langObj;
        } else if (objToFill[key] === null) {
          continue;
        } else {
          fillLanguageContent(objToFill[key]);
        }
      }
    }
  }

  fillLanguageContent(newObj);
  return newObj;
}

function recursiveFillMissingContent(objA, objB, supported_languages) {
  for (let key in objB) {
    // Handle arrays
    if (Array.isArray(objB[key])) {
      if (!objA.hasOwnProperty(key)) {
        objA[key] = JSON.parse(JSON.stringify(objB[key]));
      }
      objA[key].forEach((itemA, index) => {
        if (objB[key][index]) {
          recursiveFillMissingContent(
            itemA,
            objB[key][index],
            supported_languages
          );
        }
      });
      continue;
    }

    // Handle objects
    if (typeof objB[key] === "object" && objB[key] !== null) {
      if (!objA.hasOwnProperty(key)) {
        objA[key] = JSON.parse(JSON.stringify(objB[key]));
      }

      if (isLanguageObject(objB[key])) {
        // For language objects, ensure all supported languages exist
        const langObj = objA[key];

        // Remove unsupported languages
        for (let lang in langObj) {
          if (!supported_languages.includes(lang)) {
            delete langObj[lang];
          }
        }

        // Add missing supported languages
        supported_languages.forEach((lang) => {
          if (!langObj[lang]) {
            langObj[lang] = objB[key][lang] || "";
          }
        });
      } else {
        recursiveFillMissingContent(objA[key], objB[key], supported_languages);
      }
      continue;
    }

    // Handle primitive values
    if (!objA.hasOwnProperty(key)) {
      objA[key] = objB[key];
    }
  }
}

function recursiveTranslateChinese(obj) {
  // Create a deep copy of the object at the top level
  const newObj = JSON.parse(JSON.stringify(obj));

  function translateContent(objToTranslate) {
    for (let key in objToTranslate) {
      // Handle arrays
      if (Array.isArray(objToTranslate[key])) {
        objToTranslate[key].forEach((item, index) => {
          translateContent(item);
        });
        continue;
      }

      // Handle objects
      if (
        typeof objToTranslate[key] === "object" &&
        objToTranslate[key] !== null
      ) {
        if (isLanguageObject(objToTranslate[key])) {
          const langObj = { ...objToTranslate[key] };
          if (
            langObj.tc &&
            (langObj.sc === null ||
              langObj.sc === undefined ||
              langObj.sc === "")
          ) {
            // Translate tc to sc
            langObj.sc = translateToSimplifiedChinese(langObj.tc);
            objToTranslate[key] = langObj;
          }
        } else if (objToTranslate[key] === null) {
          continue;
        } else {
          translateContent(objToTranslate[key]);
        }
      }
    }
  }

  translateContent(newObj);
  return newObj;
}

/**
 * Recursively processes an object to convert Quill HTML classes to inline styles
 * @param {Object|Array} obj - The object to process
 * @return {Object|Array} - A new object with processed HTML content
 */
function recursiveReplaceQuillSource(obj) {
  // Create a deep copy of the object
  const newObj = JSON.parse(JSON.stringify(obj));

  // Fields that are likely to contain HTML content
  const htmlFieldNames = [
    "description",
    "details",
    "prompt",
    "hint",
    "acknowledgment",
    "help",
    "content",
    "text",
    "html",
    "body",
    "message",
    "instruction",
    "explanation",
    "note",
  ];

  // Helper function to determine if a field likely contains HTML
  function isLikelyHtml(value, key) {
    if (typeof value !== "string") return false;

    // Check if the field name suggests HTML content
    const keyLower = key.toLowerCase();
    const isHtmlField = htmlFieldNames.some((field) =>
      keyLower.includes(field)
    );

    // Check for HTML tags
    const containsHtmlTags = /<[a-z][\s\S]*>/i.test(value);

    // Check for specific Quill classes
    const containsQuillClasses = /class="[^"]*ql-[^"]*"/i.test(value);

    return isHtmlField || containsHtmlTags || containsQuillClasses;
  }

  // Recursive function to process all properties
  function processObject(objToProcess) {
    if (!objToProcess || typeof objToProcess !== "object") return objToProcess;

    // Handle different types of objects
    if (Array.isArray(objToProcess)) {
      return objToProcess.map((item) => processObject(item));
    }

    // Process regular objects
    const result = {};
    for (const key in objToProcess) {
      if (Object.prototype.hasOwnProperty.call(objToProcess, key)) {
        const value = objToProcess[key];

        if (typeof value === "string" && isLikelyHtml(value, key)) {
          // Process HTML content
          result[key] = convertQuillAlignmentToInlineStyles(value);
        } else if (typeof value === "object" && value !== null) {
          // Recursively process nested objects
          result[key] = processObject(value);
        } else {
          // Copy non-object values directly
          result[key] = value;
        }
      }
    }

    return result;
  }

  return processObject(newObj);
}

const migrateFormStructure = (formData) => {
  if (!formData) return formData;

  const updatedForm = JSON.parse(JSON.stringify(formData));
  const supported_languages = [...updatedForm.supported_languages];
  const purpose = updatedForm.purpose;
  const newForm = prepareNewFormMetadata(purpose);

  // Handle questions separately
  const existingQuestions = updatedForm.questions || [];
  delete updatedForm.questions;

  // Fill missing content for form structure
  recursiveFillMissingContent(updatedForm, newForm, supported_languages);

  // Migrate questions
  updatedForm.questions = existingQuestions.map((question) => {
    // Create a default question of the same type
    const defaultQuestion = createDefaultQuestion(
      supported_languages,
      purpose,
      question.type
    );

    // Create a new question object
    const migratedQuestion = { ...question };

    // Fill missing content
    recursiveFillMissingContent(
      migratedQuestion,
      defaultQuestion,
      supported_languages
    );

    // Handle options if they exist
    if (migratedQuestion.options) {
      migratedQuestion.options = migratedQuestion.options.map((option) => {
        const defaultOption = createDefaultOption(supported_languages);
        const migratedOption = { ...option };
        recursiveFillMissingContent(
          migratedOption,
          defaultOption,
          supported_languages
        );
        return migratedOption;
      });
    }

    return migratedQuestion;
  });

  return updatedForm;
};

export {
  createDefaultOption,
  createDefaultQuestion,
  createTemplateQuestion,
  generateObjectId,
  migrateFormStructure,
  prepareNewFormMetadata,
  recursiveFillMissingLanguageContent,
  recursiveTranslateChinese,
  recursiveReplaceQuillSource,
};
